// src/components/Visualization/InsightGenerator.js
import React, { useMemo } from 'react';
import { 
  TrendingUp, TrendingDown, AlertTriangle,
  CheckCircle, AlertCircle, Info, Target
} from 'lucide-react';

/**
 * InsightGenerator - Componente para gerar insights automáticos a partir de dados
 * 
 * @param {Object} props
 * @param {Array} props.data - Dados originais para análise
 * @param {Array} props.previousData - Dados do período anterior para comparação
 * @param {Array} props.metrics - Configurações das métricas a serem analisadas
 * @param {Object} props.targets - Metas para as métricas (opcional)
 * @param {number} props.significanceThreshold - Limiar de significância para alertas (ex: 10 = 10%)
 * @param {number} props.maxInsights - Número máximo de insights a exibir
 * @param {string} props.period - Período atual (ex: "este mês", "esta semana")
 * @param {boolean} props.showPositive - Mostrar insights positivos
 * @param {boolean} props.showNegative - Mostrar insights negativos
 * @param {boolean} props.showNeutral - Mostrar insights neutros/informativos
 * @param {boolean} props.showTargets - Mostrar insights relacionados a metas
 * @param {string} props.primaryMetric - Métrica principal para destacar
 */
const InsightGenerator = ({
  data = [],
  previousData = [],
  metrics = [],
  targets = {},
  significanceThreshold = 10,
  maxInsights = 5,
  period = "este período",
  showPositive = true,
  showNegative = true,
  showNeutral = true,
  showTargets = true,
  primaryMetric = null
}) => {
  // Gera insights com base nos dados e métricas fornecidos
  const insights = useMemo(() => {
    if (!data.length || !metrics.length) {
      return [];
    }

    const allInsights = [];
    
    // Função para calcular a variação percentual
    const calculateChange = (current, previous) => {
      if (!previous) return null;
      return ((current - previous) / previous) * 100;
    };
    
    // Função para verificar se uma variação é significativa
    const isSignificant = (changePercent) => {
      return Math.abs(changePercent) >= significanceThreshold;
    };
    
    // 1. Analisar variações nas métricas principais
    metrics.forEach(metric => {
      // Se não temos configuração completa ou dados, pular
      if (!metric.key || !metric.label) return;
      
      // Calcular totais/médias para o período atual
      let currentValue = 0;
      let previousValue = 0;
      
      // Dependendo do tipo de métrica, calculamos de forma diferente
      if (metric.type === 'sum' || !metric.type) {
        // Soma dos valores
        currentValue = data.reduce((sum, item) => sum + (item[metric.key] || 0), 0);
        previousValue = previousData.reduce((sum, item) => sum + (item[metric.key] || 0), 0);
      } else if (metric.type === 'avg') {
        // Média dos valores
        currentValue = data.reduce((sum, item) => sum + (item[metric.key] || 0), 0) / (data.length || 1);
        previousValue = previousData.reduce((sum, item) => sum + (item[metric.key] || 0), 0) / (previousData.length || 1);
      } else if (metric.type === 'max') {
        // Valor máximo
        currentValue = Math.max(...data.map(item => item[metric.key] || 0));
        previousValue = Math.max(...previousData.map(item => item[metric.key] || 0));
      } else if (metric.type === 'min') {
        // Valor mínimo
        currentValue = Math.min(...data.map(item => item[metric.key] || 0));
        previousValue = Math.min(...previousData.map(item => item[metric.key] || 0));
      }
      
      // Calcular a variação percentual
      const changePercent = calculateChange(currentValue, previousValue);
      
      // Se temos variação significativa e dados do período anterior
      if (previousData.length > 0 && changePercent !== null && isSignificant(changePercent)) {
        // Formatar valor usando a função de formatação da métrica, se disponível
        const formatValue = metric.format || (value => value.toFixed(2));
        
        // Verificar se a variação é positiva ou negativa
        const isPositive = changePercent > 0;
        
        // Decidir se esta variação é boa ou ruim com base na configuração da métrica
        const isGood = metric.higherIsBetter ? isPositive : !isPositive;
        
        // Criar mensagem de insight
        let message = `${metric.label} ${isPositive ? 'aumentou' : 'diminuiu'} ${Math.abs(changePercent).toFixed(1)}% ${period} `;
        message += `(${formatValue(currentValue)} vs ${formatValue(previousValue)})`;
        
        // Adicionar explicação adicional se disponível
        if (metric.getExplanation) {
          message += ` - ${metric.getExplanation(changePercent, currentValue, previousValue)}`;
        }
        
        // Adicionar à lista de insights
        allInsights.push({
          message,
          type: isGood ? 'positive' : 'negative',
          metric: metric.key,
          change: changePercent,
          priority: Math.abs(changePercent) * (metric.key === primaryMetric ? 2 : 1)
        });
      }
      
      // 2. Verificar metas se disponíveis
      if (showTargets && targets[metric.key] !== undefined) {
        const target = targets[metric.key];
        const percentToTarget = ((currentValue - target) / target) * 100;
        
        if (Math.abs(percentToTarget) > 5) {  // Pelo menos 5% de diferença da meta
          let targetMessage = '';
          
          if (percentToTarget > 0) {
            targetMessage = `${metric.label} está ${percentToTarget.toFixed(1)}% acima da meta`;
          } else {
            targetMessage = `${metric.label} está ${Math.abs(percentToTarget).toFixed(1)}% abaixo da meta`;
          }
          
          // Adicionar valor atual e meta
          targetMessage += ` (atual: ${metric.format ? metric.format(currentValue) : currentValue.toFixed(2)}, meta: ${metric.format ? metric.format(target) : target.toFixed(2)})`;
          
          allInsights.push({
            message: targetMessage,
            type: percentToTarget > 0 ? 'target-exceeded' : 'target-missed',
            metric: metric.key,
            change: percentToTarget,
            priority: Math.abs(percentToTarget) * 0.8 * (metric.key === primaryMetric ? 2 : 1)
          });
        }
      }
    });
    
    // 3. Adicionar análises de correlação ou padrões (se necessário)
    
    // Filtrar por tipos de insight a mostrar
    let filteredInsights = allInsights.filter(insight => {
      if (insight.type === 'positive' && !showPositive) return false;
      if (insight.type === 'negative' && !showNegative) return false;
      if (insight.type === 'neutral' && !showNeutral) return false;
      if ((insight.type === 'target-exceeded' || insight.type === 'target-missed') && !showTargets) return false;
      return true;
    });
    
    // Ordenar por prioridade e limitar ao número máximo
    return filteredInsights
      .sort((a, b) => b.priority - a.priority)
      .slice(0, maxInsights);
  }, [data, previousData, metrics, targets, significanceThreshold, primaryMetric, period, maxInsights, showPositive, showNegative, showNeutral, showTargets]);

  // Seleciona o ícone apropriado para o tipo de insight
  const getInsightIcon = (type) => {
    switch (type) {
      case 'positive':
        return <TrendingUp className="h-5 w-5 text-green-500" />;
      case 'negative':
        return <TrendingDown className="h-5 w-5 text-red-500" />;
      case 'neutral':
        return <Info className="h-5 w-5 text-blue-500" />;
      case 'target-exceeded':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'target-missed':
        return <Target className="h-5 w-5 text-yellow-500" />;
      case 'alert':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  // Retorna a classe de cor apropriada para o tipo de insight
  const getInsightColorClass = (type) => {
    switch (type) {
      case 'positive':
        return 'bg-green-50 border-green-200';
      case 'negative':
        return 'bg-red-50 border-red-200';
      case 'neutral':
        return 'bg-blue-50 border-blue-200';
      case 'target-exceeded':
        return 'bg-green-50 border-green-200';
      case 'target-missed':
        return 'bg-yellow-50 border-yellow-200';
      case 'alert':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  // Se não houver insights, exibir uma mensagem
  if (!insights.length) {
    return (
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center text-gray-500 text-sm">
        Não há insights significativos para exibir neste momento.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {insights.map((insight, index) => (
        <div 
          key={`insight-${index}`} 
          className={`p-3 rounded-lg border ${getInsightColorClass(insight.type)} flex items-start`}
        >
          <div className="flex-shrink-0 mr-3">
            {getInsightIcon(insight.type)}
          </div>
          <div className="flex-1 text-sm">
            {insight.message}
          </div>
        </div>
      ))}
    </div>
  );
};

export default InsightGenerator;
